import {
  Dropdown,
  Field,
  makeStyles,
  Option,
  Switch,
  tokens,
} from '@fluentui/react-components';
import { useT } from '@powerportalspro/react';
import {
  DateTimeEdit,
  DateTimeEditorType,
  Section,
  SectionColumn,
} from '@powerportalspro/react-fluent';
import { useCallback, useMemo, useState } from 'react';

// Localization namespace — reuses the Blazor demo's
// `DateEditPropertiesDemo` keys so both stacks read from the same JSON.
const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Editors.DateEdit.DateEditPropertiesDemo';

const EDITOR_TYPE_VALUES = [
  DateTimeEditorType.DateTime,
  DateTimeEditorType.DateOnly,
  DateTimeEditorType.TimeOnly,
] as const;

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
  },
  inlineField: {
    flexShrink: 0,
    gridTemplateColumns: 'auto auto',
    '& label': {
      whiteSpace: 'nowrap',
    },
  },
  selectInput: {
    minWidth: '125px',
  },
  note: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

function cap(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function DateEditPropertiesSample() {
  const styles = useStyles();
  const t = useT();
  const [readOnly, setReadOnly] = useState(false);
  const [required, setRequired] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [editorType, setEditorType] = useState<DateTimeEditorType>(DateTimeEditorType.DateTime);

  const editorTypeOptions = useMemo(
    () =>
      EDITOR_TYPE_VALUES.map((value) => ({
        value,
        // Localization keys use the C# enum names (`DateTime`, `DateOnly`,
        // `TimeOnly`) — capitalize the lowercase value to match.
        label: t(`${STRINGS_BASE}.dateTimeEditorType.${cap(value)}`),
      })),
    [t],
  );

  // Disable future dates — mirrors the Blazor demo's `DateDisabled`.
  const disabledDateFunc = useCallback((d: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d > today;
  }, []);

  return (
    <Section>
      <SectionColumn
        header={
          <div className={styles.toolbar}>
            <Switch
              label={t(`${STRINGS_BASE}.readonly`)}
              labelPosition="before"
              checked={readOnly}
              onChange={(_, d) => setReadOnly(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.required`)}
              labelPosition="before"
              checked={required}
              onChange={(_, d) => setRequired(d.checked)}
            />
            <Switch
              label={t(`${STRINGS_BASE}.disabled`)}
              labelPosition="before"
              checked={disabled}
              onChange={(_, d) => setDisabled(d.checked)}
            />
            <Field
              label={t(`${STRINGS_BASE}.date-time-editor-type`)}
              orientation="horizontal"
              className={styles.inlineField}
            >
              <Dropdown
                className={styles.selectInput}
                value={
                  editorTypeOptions.find((o) => o.value === editorType)?.label ?? editorType
                }
                selectedOptions={[editorType]}
                onOptionSelect={(_, d) => {
                  if (d.optionValue) setEditorType(d.optionValue as DateTimeEditorType);
                }}
              >
                {editorTypeOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          </div>
        }
      >
        <div
          className={styles.note}
          dangerouslySetInnerHTML={{ __html: t(`${STRINGS_BASE}.future-date-note`) }}
        />
        <div style={{ maxWidth: '320px' }}>
          <DateTimeEdit
            columnName="ppp_lastonholddate"
            required={required}
            readOnly={readOnly}
            disabled={disabled}
            disabledDateFunc={disabledDateFunc}
            editorType={editorType}
          />
        </div>
      </SectionColumn>
    </Section>
  );
}
